import React, { useMemo } from 'react';
import { CreditRequestBatchLineItemFieldsFragment } from '@/types';
import { CreditRequestListItem } from './credit-request-list-item';
import { formatCurrency } from '@/utils/money';
import { getTranslationValue } from '@/utils/translations';
import { ItemSubtitle } from '@/components/item-subtitle';
import { Props as ListItemProps, ListOption } from '@/components/list-item';
import { RecyclerList } from '@/components/recycler-list';
import { ScrollViewProps } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';
import { usePaymentsContext } from '@/contexts/payments-context';

interface CreditRequestBatchLineItemMetadata {
  imageURLs?: string[];
  itemName: string | null;
  packPriceInMicros: number;
  quantity: number;
  reason: string;
  unit: string;
}

const getCreditRequestBatchLineItemMetadata = (
  option: CreditRequestBatchLineItemFieldsFragment,
): CreditRequestBatchLineItemMetadata => {
  return {
    imageURLs: option.photo_url_list,
    itemName: option.variant_pack.item.display_name || null,
    packPriceInMicros: option.pack_price_in_micros,
    quantity: option.quantity,
    reason: getTranslationValue(
      option.credit_request_reason?.display_name.translation ?? '',
    ),
    unit: option.uom_override ?? option.variant_pack.pack.unit,
  };
};

type CreditRequestBatchLineItemOption =
  ListOption<CreditRequestBatchLineItemMetadata>;

interface Props {
  creditRequestBatchLineItems: CreditRequestBatchLineItemFieldsFragment[];
  scrollViewProps: ScrollViewProps;
}

export const CreditRequestDetailList: React.FC<Props> = ({
  creditRequestBatchLineItems,
  scrollViewProps,
}) => {
  const { currencyCode } = usePaymentsContext();
  const { isLargeLayout } = useLayoutContext();

  const sections = useMemo(
    () => [
      {
        data: creditRequestBatchLineItems.map<CreditRequestBatchLineItemOption>(
          (li) => {
            const metadata = getCreditRequestBatchLineItemMetadata(li);
            return {
              metadata,
              title:
                // Use reason in place for missing itemName on large screens
                metadata.itemName || (isLargeLayout ? metadata.reason : ''),
              value: metadata.reason,
            };
          },
        ),
        key: null,
      },
    ],
    [creditRequestBatchLineItems, isLargeLayout],
  );

  const listItemProps: Partial<
    ListItemProps<CreditRequestBatchLineItemOption>
  > = {
    listItemStyle: {
      marginBottom: 8,
      paddingVertical: 8,
    },
    renderMainContent: (option) => {
      const metadata = option.metadata!;
      return (
        <CreditRequestListItem
          formattedPrice={formatCurrency(
            metadata.packPriceInMicros * metadata.quantity,
            currencyCode,
          )}
          imageURLs={metadata.imageURLs}
          quantity={metadata.quantity}
          subtitle={
            <ItemSubtitle
              currencyCode={currencyCode}
              displayString={metadata.unit}
              showInlineModifiers
              sku={metadata.reason}
              unitCount={1}
              unitPriceInMicros={metadata.packPriceInMicros}
            />
          }
          title={option.title}
        />
      );
    },
  };

  return (
    <RecyclerList
      estimatedItemSize={[140, 120, 120, 90]}
      listItemProps={listItemProps}
      sections={sections}
      shouldRenderSeparator={() => false}
      testID="credit-request-detail-list"
      {...scrollViewProps}
    />
  );
};
